import { type components } from "@njbsoft/types/customer";
import { dateOf } from "./ordering";
import { present } from "./present";
import { type Tone } from "./tone";

type CustomerInstallation = components["schemas"]["CustomerInstallation"];
type ComplianceVerdict = components["schemas"]["ComplianceVerdict"];
type CatalogAssemblyType = components["schemas"]["CatalogAssemblyType"];

const ASSEMBLY_TYPE_LABEL: Record<CatalogAssemblyType, string> = {
  AVB: "AVB",
  DC: "DC",
  DCDA: "DCDA",
  DCDA_II: "DCDA-II",
  PVB: "PVB",
  RP: "RP",
  RPDA: "RPDA",
  RPDA_II: "RPDA-II",
  SVB: "SVB",
};

function isAssemblyType(value: string): value is CatalogAssemblyType {
  return Object.hasOwn(ASSEMBLY_TYPE_LABEL, value);
}

/** The assembly type as a nameplate prints it; null for anything outside the catalog vocabulary. */
export function assemblyTypeLabel(type: string | null | undefined): string | null {
  return type && isAssemblyType(type) ? ASSEMBLY_TYPE_LABEL[type] : null;
}

function joinPresent(parts: readonly (string | null)[], separator: string): string {
  return parts.filter((part): part is string => !!part).join(separator);
}

/**
 * The manufacturer's name before its first " - ". Catalog display names carry a
 * corporate suffix ("Watts - A Watts Water Technologies Company") that would
 * run every title long. The full name stays when nothing precedes the
 * separator.
 */
function makeName(label: string | null | undefined): string | null {
  return present(label?.split(" - ", 1)[0]) ?? present(label);
}

/**
 * A device's name, "{size} {type}, {make} {model}", with every missing or blank
 * part dropped and the comma only between two non-empty groups. The make is
 * the manufacturer's name without its corporate suffix (the nameplate prints
 * the full one). The parts are data, never translated.
 */
export function deviceLabel(parts: {
  sizeLabel?: string | null;
  assemblyType?: string | null;
  manufacturerLabel?: string | null;
  modelLabel?: string | null;
}): string {
  const kind = joinPresent([present(parts.sizeLabel), assemblyTypeLabel(parts.assemblyType)], " ");
  const make = joinPresent([makeName(parts.manufacturerLabel), present(parts.modelLabel)], " ");
  return joinPresent([kind, make], ", ");
}

export type StatusKind =
  | "failed"
  | "pastDue"
  | "neverTested"
  | "underReview"
  | "onSchedule"
  | "outOfService"
  | "removed";

const VERDICT_KIND: Record<ComplianceVerdict, StatusKind> = {
  compliant: "onSchedule",
  overdue: "pastDue",
  failed: "failed",
  unverified: "neverTested",
  pending_review: "underReview",
  out_of_service: "outOfService",
  terminal: "removed",
};

function isVerdict(value: string): value is ComplianceVerdict {
  return Object.hasOwn(VERDICT_KIND, value);
}

/**
 * The status a verdict reads as. The wire types `verdict` as a nullable string,
 * and a device the portal cannot classify reads as never tested, never as on
 * schedule.
 */
export function classifyVerdict(verdict: string | null | undefined): StatusKind {
  return verdict && isVerdict(verdict) ? VERDICT_KIND[verdict] : "neverTested";
}

/** The statuses the portal counts: every kind but out of service and removed. */
export type CountedKind = Exclude<StatusKind, "outOfService" | "removed">;

/**
 * Whether a device in this status counts toward an account's standing and is
 * offered for a test. Out of service and removed have left the schedule.
 */
export function isCounted(kind: StatusKind): kind is CountedKind {
  return kind !== "outOfService" && kind !== "removed";
}

export interface OpenRequestRef {
  requestId: string;
  createdAt: string;
}

/** `word`, `sentence` and `dateLine` are i18n keys in the `customer` namespace. */
export interface DeviceStatus {
  kind: StatusKind;
  tone: Tone;
  word: string;
  sentence: string;
  dateLine: string | null;
  date: string | null;
  requested: OpenRequestRef | null;
}

export const STATUS_WORD_KEY: Record<StatusKind | "passed", string> = {
  failed: "status.word.failed",
  pastDue: "status.word.pastDue",
  neverTested: "status.word.neverTested",
  underReview: "status.word.underReview",
  onSchedule: "status.word.onSchedule",
  passed: "status.word.passed",
  outOfService: "status.word.outOfService",
  removed: "status.word.removed",
};

export const STATUS_TONE: Record<StatusKind | "passed", Tone> = {
  failed: "red",
  pastDue: "red",
  neverTested: "gray",
  underReview: "blue",
  onSchedule: "green",
  passed: "green",
  outOfService: "gray",
  removed: "gray",
};

// A dated status reads its date from one field and falls back to its dateless
// sentence, with no date line, when that field is empty. An undated status has
// one sentence whatever dates the row carries.
type StatusLine =
  | {
      source: "last_test_date" | "next_test_due_date";
      sentence: string;
      noDateSentence: string;
      dateLine: string;
    }
  | { source: null; sentence: string; dateLine: string | null };

const STATUS_LINE: Record<StatusKind, StatusLine> = {
  failed: {
    source: "last_test_date",
    sentence: "status.sentence.failed",
    noDateSentence: "status.sentence.failedNoDate",
    dateLine: "status.dateLine.tested",
  },
  pastDue: {
    source: "next_test_due_date",
    sentence: "status.sentence.pastDue",
    noDateSentence: "status.sentence.pastDueNoDate",
    dateLine: "status.dateLine.due",
  },
  neverTested: {
    source: null,
    sentence: "status.sentence.neverTested",
    dateLine: "status.dateLine.none",
  },
  underReview: {
    source: "last_test_date",
    sentence: "status.sentence.underReview",
    noDateSentence: "status.sentence.underReviewNoDate",
    dateLine: "status.dateLine.tested",
  },
  onSchedule: {
    source: "next_test_due_date",
    sentence: "status.sentence.onSchedule",
    noDateSentence: "status.sentence.onScheduleNoDate",
    dateLine: "status.dateLine.due",
  },
  outOfService: { source: null, sentence: "status.sentence.outOfService", dateLine: null },
  removed: { source: null, sentence: "status.sentence.removed", dateLine: null },
};

/**
 * A device's status as a color, a word and a date: the kind its verdict reads
 * as, the date that kind is told by (the last test for failed and under review,
 * the due date for past due and on schedule), and the open request it is
 * handed, echoed for the row's request line.
 */
export function deviceStatus(
  installation: Pick<CustomerInstallation, "verdict" | "next_test_due_date" | "last_test_date">,
  openRequest?: OpenRequestRef | null,
): DeviceStatus {
  const kind = classifyVerdict(installation.verdict);
  const line = STATUS_LINE[kind];
  const base = {
    kind,
    tone: STATUS_TONE[kind],
    word: STATUS_WORD_KEY[kind],
    requested: openRequest ?? null,
  };
  if (line.source === null) {
    return { ...base, sentence: line.sentence, dateLine: line.dateLine, date: null };
  }
  const date = dateOf(installation[line.source]);
  return {
    ...base,
    sentence: date ? line.sentence : line.noDateSentence,
    dateLine: date ? line.dateLine : null,
    date,
  };
}
